export type Bit = 0 | 1;
export type Basis = "X" | "+";

export interface QuantumExchangeResponse {
  success: boolean;
  aliceBits: Bit[];
  aliceBases: Basis[];
  photonsForBob: number[];
  error?: string;
}

const API_BASE = (process.env.REACT_APP_QUANTUM_API_BASE || "/api").replace(/\/$/, "");

const buildEndpointCandidates = (): string[] => {
  const candidates = [
    `${API_BASE}/quantum_channel`,
    `${API_BASE}/quantum_channel.py`,
    "/api/quantum_channel",
    "/api/quantum_channel.py",
    "/quantum_channel",
  ];

  return Array.from(new Set(candidates));
};

export const QuantumKeyService = {
  async generateAndTransmit(length: number = 256, eveActive: boolean = false): Promise<QuantumExchangeResponse> {
    const endpointCandidates = buildEndpointCandidates();

    try {
      const aliceBits: Bit[] = Array.from({ length }, () => (Math.random() > 0.5 ? 1 : 0));
      const aliceBases: Basis[] = Array.from({ length }, () => (Math.random() > 0.5 ? "X" : "+"));

      const requestBody = JSON.stringify({
        bits: aliceBits,
        bases: aliceBases,
        eavesdropperActive: eveActive,
      });

      let lastErrorMessage = "Unable to reach quantum channel endpoint.";
      let data: { received_states?: number[] } | null = null;

      for (const endpoint of endpointCandidates) {
        const response = await fetch(endpoint, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: requestBody,
        });

        if (response.ok) {
          data = (await response.json()) as { received_states?: number[] };
          break;
        }

        const errorBody = await response.text();
        lastErrorMessage = `Quantum channel failed at ${endpoint} (${response.status}): ${errorBody}`;

        // Keep trying fallbacks when the route does not exist.
        if (response.status !== 404) {
          break;
        }
      }

      if (!data) {
        throw new Error(lastErrorMessage);
      }

      return {
        success: true,
        aliceBits,
        aliceBases,
        photonsForBob: data.received_states || [],
      };
    } catch (error) {
      return {
        success: false,
        aliceBits: [],
        aliceBases: [],
        photonsForBob: [],
        error: error instanceof Error ? error.message : "Unknown quantum channel error",
      };
    }
  },

  deriveFinalKey(bits: Bit[], myBases: Basis[], theirBases: Basis[]): Bit[] {
    const siftedKey: Bit[] = [];
    for (let i = 0; i < myBases.length; i += 1) {
      if (myBases[i] === theirBases[i]) {
        siftedKey.push(bits[i]);
      }
    }
    return siftedKey;
  },

  formatToHex(rawBits: Bit[]): string {
    let targetBits = [...rawBits];

    if (targetBits.length > 256) {
      targetBits = targetBits.slice(0, 256);
    }
    while (targetBits.length < 256) {
      targetBits.push(0);
    }

    let hexString = "";
    for (let i = 0; i < targetBits.length; i += 4) {
      const chunk = targetBits.slice(i, i + 4).join("");
      hexString += parseInt(chunk, 2).toString(16);
    }

    return hexString;
  },
};
